import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import './AppManagement.css';
import API_URL from '../config';

const AppManagement = () => {
  const { token } = useAuth();
  const [apps, setApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [appUsers, setAppUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Fetch all apps
  useEffect(() => {
    const fetchApps = async () => {
      try {
        const res = await fetch(`${API_URL}/apps`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setApps(data);
      } catch (err) {
        console.error('Failed to fetch apps:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchApps();
  }, [token]);

  // Fetch all Okta users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_URL}/okta/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        setAllUsers(data.filter(u => u.status === 'ACTIVE'));
      } catch (err) {
        console.error('Failed to fetch users:', err);
      }
    };
    fetchUsers();
  }, [token]);

  // Fetch app users
  const fetchAppUsers = useCallback(async (appId) => {
    setUsersLoading(true);
    setMessage('');
    setError('');
    try {
      const res = await fetch(`${API_URL}/apps/${appId}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setAppUsers(data);
    } catch (err) {
      console.error('Failed to fetch app users:', err);
    } finally {
      setUsersLoading(false);
    }
  }, [token]);

  const handleSelectApp = (app) => {
    setSelectedApp(app);
    setSelectedUserId('');
    fetchAppUsers(app.id);
  };

  const handleAssignUser = async () => {
    if (!selectedUserId) return;
    setMessage('');
    setError('');

    try {
      const res = await fetch(`${API_URL}/apps/${selectedApp.id}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ userId: selectedUserId })
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      setSelectedUserId('');
      fetchAppUsers(selectedApp.id);
    } catch (err) {
      setError('Failed to assign user');
    }
  };

  const handleRemoveUser = async (userId) => {
    setMessage('');
    setError('');

    try {
      const res = await fetch(`${API_URL}/apps/${selectedApp.id}/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message); return; }
      setMessage(data.message);
      fetchAppUsers(selectedApp.id);
    } catch (err) {
      setError('Failed to remove user');
    }
  };

  return (
    <Layout>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#003366' }}>App Management</h1>
        <p style={{ fontSize: '14px', color: '#666666', marginTop: '4px' }}>
          Manage ABC Corp application assignments in Okta
        </p>
      </div>

      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="apps-layout">
        {/* Apps Sidebar */}
        <div className="apps-sidebar">
          <h2>ABC Corp Apps</h2>
          {loading ? (
            <p style={{ padding: '16px', fontSize: '14px', color: '#999' }}>Loading...</p>
          ) : (
            apps.map(app => (
              <div
                key={app.id}
                className={`app-item ${selectedApp?.id === app.id ? 'active' : ''}`}
                onClick={() => handleSelectApp(app)}
              >
                <div className="app-item-name">{app.label}</div>
                <div className="app-item-status">{app.status}</div>
              </div>
            ))
          )}
        </div>

        {/* App Detail */}
        <div className="app-detail">
          {!selectedApp ? (
            <div className="no-selection">
              ← Select an app to manage its users
            </div>
          ) : (
            <>
              <div className="app-detail-header">
                <h2>{selectedApp.label}</h2>
                <span className="user-count">{appUsers.length} assigned users</span>
              </div>

              {usersLoading ? (
                <p style={{ fontSize: '14px', color: '#999' }}>Loading users...</p>
              ) : appUsers.length === 0 ? (
                <div className="empty-state">No users assigned to this app yet.</div>
              ) : (
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Full Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appUsers.map((user, index) => (
                      <tr key={user.id}>
                        <td>{index + 1}</td>
                        <td>{user.firstName} {user.lastName}</td>
                        <td>{user.email}</td>
                        <td>{user.status}</td>
                        <td>
                          <button
                            className="btn-remove"
                            onClick={() => handleRemoveUser(user.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="add-user-section">
                <select
                  value={selectedUserId}
                  onChange={e => setSelectedUserId(e.target.value)}
                >
                  <option value="">-- Assign a user to this app --</option>
                  {allUsers
                    .filter(u => !appUsers.find(au => au.id === u.id))
                    .map(u => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName} ({u.email})
                      </option>
                    ))
                  }
                </select>
                <button
                  className="btn-primary"
                  onClick={handleAssignUser}
                  disabled={!selectedUserId}
                >
                  Assign User
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AppManagement;